// phase-compiler-tab-pins (Kimi D2) — fetch the authoritative per-submission
// navigation capability map (documentId → inspectable PDF). The Bookmarks & Links
// first-applicable auto-select + Previous/Next filter over this instead of guessing
// client-side. Scoped to the submission; a slow refresh for an old submission can
// never overwrite a newer one (request epoch + submission guard).
//
// GENERATION-AWARE staleness (closeout G1): `stale` must be SYNCHRONOUS — true on the
// very commit an invalidation happens, BEFORE the refetch effect runs — or the
// auto-select effect (another useEffect) could act on the prior generation's map in
// the one-commit window before `fetching` flips. The current generation is
// `submissionId | reloadKey | entryKey`; the stored map carries the generation it was
// fetched for. When they differ, `stale` is true the same render (and a refetch is
// scheduled). `reloadKey` bumps on document reloads (upload/convert/reclassify…);
// `entryKey` bumps when the caller re-enters navigation (hidden→visible). The last
// map is retained across a refresh and across a recoverable error so an open
// editor/draft is never torn down; `refreshing` / `error` drive an inline status.

import { useCallback, useEffect, useRef, useState } from 'react';
import { parseApiErrorBody } from '@/api/v2Client';
import { submissionNavigationCapability } from '@/api/documentNavigation';

export type NavigationCapabilityMap = Readonly<Record<string, boolean>>;

export interface UseNavigationCapabilityResult {
  /** The last-known capability map, or null until the first load resolves. */
  readonly map: NavigationCapabilityMap | null;
  /** True only during the FIRST load (no map yet, no error). */
  readonly loading: boolean;
  /** True while a refresh is pending/in flight with a prior map still shown. */
  readonly refreshing: boolean;
  readonly error: string | null;
  /**
   * The map is NOT a fresh authoritative snapshot for the CURRENT generation right
   * now — it is loading, a refresh is pending/in flight (generation changed), the
   * last fetch errored, or nothing has loaded. Derived SYNCHRONOUSLY so a caller's
   * effect in the same commit never acts on a prior generation's candidates.
   */
  readonly stale: boolean;
  readonly refetch: () => Promise<void>;
}

interface State {
  submissionId: string | null;
  map: NavigationCapabilityMap | null;
  fetching: boolean;
  error: string | null;
  /** The generation this map/error was produced for (null until first resolve). */
  gen: string | null;
}

export function useNavigationCapability(
  submissionId: string | null | undefined,
  reloadKey: number = 0,
  entryKey: number = 0,
): UseNavigationCapabilityResult {
  const id = submissionId ?? null;
  const currentGen = `${id ?? ''}|${reloadKey}|${entryKey}`;

  const [state, setState] = useState<State>({ submissionId: id, map: null, fetching: id !== null, error: null, gen: null });
  const request = useRef(0);
  const mounted = useRef(false);
  const idRef = useRef(id);
  idRef.current = id;
  const genRef = useRef(currentGen);
  genRef.current = currentGen;

  const refetch = useCallback(async (): Promise<void> => {
    const fetchId = idRef.current;
    const fetchGen = genRef.current;
    if (fetchId === null) {
      if (mounted.current) setState({ submissionId: null, map: null, fetching: false, error: null, gen: fetchGen });
      return;
    }
    const epoch = ++request.current;
    const live = () => mounted.current && epoch === request.current && idRef.current === fetchId;
    // Retain the prior map (same submission) so an open editor survives the refresh;
    // clear only when the submission itself changed.
    setState((prev) => prev.submissionId === fetchId
      ? { ...prev, fetching: true, error: null }
      : { submissionId: fetchId, map: null, fetching: true, error: null, gen: null });
    try {
      const res = await submissionNavigationCapability.get(fetchId);
      if (!live()) return;
      setState({ submissionId: fetchId, map: res.documents, fetching: false, error: null, gen: fetchGen });
    } catch (err) {
      if (!live()) return;
      const body = parseApiErrorBody(err);
      // Keep the last map on error (no false blank); `error` + `stale` disable auto
      // actions and surface a retry. `gen` stays as the retained map's gen → stale.
      setState((prev) => ({ submissionId: fetchId, map: prev.submissionId === fetchId ? prev.map : null, fetching: false, error: body?.message ?? body?.error ?? (err instanceof Error ? err.message : String(err)), gen: prev.submissionId === fetchId ? prev.gen : null }));
    }
  }, []);

  useEffect(() => {
    mounted.current = true;
    void refetch();
    return () => {
      mounted.current = false;
      // Invalidate any in-flight request so a late resolve cannot apply after unmount.
      // eslint-disable-next-line react-hooks/exhaustive-deps
      request.current++;
    };
    // `currentGen` is the invalidation trigger: a change re-runs this effect → refetch.
  }, [refetch, currentGen]);

  const match = state.submissionId === id;
  const map = match ? state.map : null;
  const fetching = match ? state.fetching : id !== null;
  const error = match ? state.error : null;
  const genMatches = match && state.gen === currentGen;
  const loading = id !== null && map === null && error === null;
  const refreshing = map !== null && error === null && (fetching || !genMatches);
  const stale = map === null || error !== null || fetching || !genMatches;
  return { map, loading, refreshing, error, stale, refetch };
}
